<script lang="ts">
export default {
  name: 'UICheckbox',
};
</script>

<script setup lang="ts">
/* eslint-disable tailwindcss/no-custom-classname */
import { watch, ref } from 'vue';
import { useField } from 'vee-validate';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export interface Props {
  label?: string;
  subLabel?: string;
  name: string;
  modelValue: boolean;
  tooltip?: string;
  checkboxPosition?: 'start' | 'center' | 'end';
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  label: '',
  subLabel: '',
  tooltip: '',
  checkboxPosition: 'center',
  disabled: false,
});

const { errorMessage, setValue, value: fieldValue } = useField(props.name, undefined, {
  initialValue: props.modelValue ? '1' : '0',
});

const emit = defineEmits(['update:modelValue', 'update:error']);
const input = ref();

watch(
  () => errorMessage.value,
  value => {
    emit('update:error', value);
  },
);

const updateModelValue = (event: Event) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  };

  const isChecked = Boolean(Number(fieldValue.value));
  setValue(isChecked ? '0' : '1');
  emit('update:modelValue', !isChecked);
};
</script>

<template>
  <div class="mb-2 w-full">
    <div
      class="relative flex flex-nowrap"
      :class="[
        {'!cursor-not-allowed pointer-events-none': props.disabled},
        `items-${props.checkboxPosition}`,
      ]"
    >
      <label
        :for="slugify(props.name)"
        class="relative flex items-center gap-x-2 pr-2 cursor-pointer"
      >
        <input
          :id="slugify(props.name)"
          ref="input"
          :checked="props.modelValue"
          :value="props.modelValue ? '1' : '0'"
          :name="props.name"
          :disabled="props.disabled"
          :data-test-id="slugify(props.name)"
          class="m-0 flex size-6 shrink-0 appearance-none peer"
          type="checkbox"
          @click.stop="updateModelValue"
        />
        <div
          class="absolute top-0 left-0 flex size-6 items-center justify-center rounded-lg border border-blue-700
          bg-white peer-checked:bg-blue-700"
          :class="{'opacity-30': props.disabled}"
          >
          <FontAwesomeIcon
            v-if="props.modelValue"
            :icon="['fas', 'check']"
            size="1x"
            class="z-1 relative text-white"
          />
        </div>
        <slot />
      </label>
      <label
        :for="slugify(props.name)"
        class="flex flex-col items-start cursor-pointer"
        :class="{'!cursor-not-allowed pointer-events-none': props.disabled}"
      >
        <span v-html="props.label" />
        <span v-if="props.subLabel" class="text-sm">{{props.subLabel}}</span>
      </label>
    </div>
    <span 
      v-if="errorMessage" 
      class="text-sm text-orange-900"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>
